import { normalizeHex } from '../../components/colorUtils'
import { DEFAULT_ACTIVE, DEFAULT_MAIN } from '../../app/branding'
import { parseChannel } from './settingsService'

/**
 * إعدادات النظام as a form: which save groups each section holds, and what each group's
 * fields start at. Plain data, so the hub can read these sections' state on the OTHER
 * settings page too, where no form for them exists.
 */

/** Each section's save groups (settingsService `SECTIONS`), in the order they post. */
export const MAIN_SECTIONS = {
  identity: ['brandTheme'],
  media: ['logo', 'reportLogo', 'favicon', 'loginTheme', 'sideLogin', 'backgroundLogin'],
  keys: ['appTitle', 'footer', 'organization', 'domain', 'servicesUrl'],
  contact: ['contact'],
  video: ['video'],
  userManual: ['userGuide', 'userManual', 'adminGuide'],
  email: ['email'],
  reports: ['reports'],
}

const GROUPS = Object.values(MAIN_SECTIONS).flat()

/**
 * The value the form starts with — the stored setting, or the empty string when the key
 * has never been written. Booleans the endpoint stores as text stay `"true"`/`"false"`.
 */
const pick = (settings, key, fallback = '') => settings?.[key] ?? fallback

/** The whole form, as loaded. */
export function mainInitialForm(data) {
  return Object.assign({}, ...GROUPS.map((group) => mainDefaults(group, data)))
}

/** Which keys each group owns — what makes a group dirty. */
export function mainKeys(data) {
  return Object.fromEntries(GROUPS.map((group) => [group, Object.keys(mainDefaults(group, data))]))
}

/** What a group's fields hold on the server — what Cancel puts back. */
export function mainDefaults(section, data) {
  const s = data?.settings ?? {}
  const company = data?.company ?? {}
  const columns = (key) => (pick(s, key) ? pick(s, key).split(',') : [])

  switch (section) {
    case 'brandTheme':
      return {
        brandMain: normalizeHex(data?.brand?.main) || DEFAULT_MAIN,
        brandActive: normalizeHex(data?.brand?.active) || DEFAULT_ACTIVE,
      }
    case 'logo':
      return { logo: null, logoBackground: pick(s, 'LogoBackgroundEnabled', 'true') }
    case 'reportLogo':
      return { reportLogo: null }
    case 'sideLogin':
      return { sideLoginAr: null, sideLoginEn: null }
    case 'backgroundLogin':
      return { backgroundAr: null, backgroundEn: null }
    case 'favicon':
      return { favicon: null }
    case 'loginTheme':
      return {
        theme: pick(s, 'LoginPageTheme', 'white'),
        logoOnLogin: pick(s, 'LogoOnLoginPage', 'false'),
      }
    case 'appTitle':
      return {
        arabicAppTitle: pick(s, 'Default_ArabicApplicationTitle'),
        englishAppTitle: pick(s, 'Default_EnglishApplicationTitle'),
      }
    case 'footer':
      return { footer: pick(s, 'WebsiteFooterText'), footerEn: pick(s, 'WebsiteFooterTextEn') }
    case 'organization':
      return {
        arabicOrgName: company.arabicName ?? '',
        englishOrgName: company.englishName ?? '',
        orgEmail: company.email ?? '',
      }
    case 'domain':
      return { domainUrl: pick(s, 'DomainURL'), useHsts: pick(s, 'UseHsts', 'false') }
    case 'contact':
      return {
        enableContactPage: pick(s, 'ShowSupportContactInfoPage') === 'true',
        contactPageTitle: pick(s, 'ContactPageTitle'),
        contactPageTitleEn: pick(s, 'ContactPageTitleEn'),
        contactPageHeader: pick(s, 'ContactPageHeader'),
        contactPageHeaderEn: pick(s, 'ContactPageheaderEn'),
        enableEmail: pick(s, 'ContactEmailShowSendForm') === 'true' || !!pick(s, 'ContactEmail'),
        contactEmail: pick(s, 'ContactEmail'),
        showSendForm: pick(s, 'ContactEmailShowSendForm') === 'true',
        showEmailInfo: pick(s, 'ContactEmailShowEmailInfo') === 'true',
        contactEmailLabel: pick(s, 'ContactEmailLabelText'),
        contactEmailEnLabel: pick(s, 'ContactEmailEnLabelText'),
        showPhone: pick(s, 'ContactEmailShowPhoneInfo') === 'true',
        contactPhone: pick(s, 'ContactPhone'),
        contactPhoneLabel: pick(s, 'ContactPhoneLabelText'),
        contactPhoneEnLabel: pick(s, 'ContactPhoneEnLabelText'),
      }
    case 'video':
      return {
        progressBar: pick(s, 'Default_EnablePlayerProgressbar') === 'true',
        forceSubtitle: pick(s, 'Default_EnableForceSubtitle') === 'true',
        userSubtitleControl: pick(s, 'EnableUserSubtitleControl') === 'true',
        pauseOnBlur: pick(s, 'Default_EnablePlayerBlur') === 'true',
        playbackRates: pick(s, 'Default_EnablePlayerPlaybackRates') === 'true',
      }
    case 'userGuide':
      return { guideAr: null, guideEn: null }
    case 'userManual':
      return {
        manualPopup: pick(s, 'Default_EnableUserManualPopup') === 'true',
        introVideo: pick(s, 'EnableIntroVideo') === 'true',
        guideLinkOnLogin: pick(s, 'UserGuideLinkOnTheLoginPage') === 'true',
        guideSeparatePage: pick(s, 'ShowUserGuideOnSeparatePage') === 'true',
      }
    case 'adminGuide':
      return { adminManualUrl: pick(s, 'AdminPanelUserManualUrl') }
    case 'email':
      return {
        emailHost: pick(s, 'Email_Host'),
        emailPort: pick(s, 'Email_Port'),
        emailFrom: pick(s, 'Email_From'),
        emailSenderName: pick(s, 'Email_SenderName'),
        emailUsername: pick(s, 'Email_Username'),
        emailPassword: pick(s, 'Email_Password'),
        emailSslOptions: pick(s, 'Email_SslOptions'),
        /* Stored as text in whatever case the writer used; a switch needs one spelling. */
        emailServiceState: String(pick(s, 'Email_ServiceState', 'false')).toLowerCase() === 'true' ? 'true' : 'false',
        disableCertValidation: pick(s, 'DisableMailServerCertificateValidation', 'false'),
      }
    case 'servicesUrl':
      return { servicesUrl: pick(s, 'ServicesUrl') }
    case 'whatsapp': {
      const c = parseChannel(pick(s, 'WhatsAppSettings', '{}'))
      return {
        whatsappEnabled: c.Enabled === true,
        whatsappApiUrl: c.ApiUrl ?? '',
        whatsappApiKey: c.ApiKey ?? '',
        whatsappSender: c.SenderNumber ?? '',
      }
    }
    case 'slack': {
      const c = parseChannel(pick(s, 'SlackSettings', '{}'))
      return {
        slackEnabled: c.Enabled === true,
        slackWebhook: c.WebhookUrl ?? '',
        slackBotToken: c.BotToken ?? '',
        slackChannel: c.Channel ?? '',
      }
    }
    case 'sms': {
      const c = parseChannel(pick(s, 'SmsSettings', '{}'))
      return {
        smsEnabled: c.Enabled === true,
        smsApiUrl: c.ApiUrl ?? '',
        smsApiKey: c.ApiKey ?? '',
        smsSenderId: c.SenderId ?? '',
      }
    }
    case 'reports':
      return {
        userStatistics: pick(s, 'User_Statistics_IsEnabled').toLowerCase() === 'true',
        policiesColumns: columns('Reports_Policies_Profile_Columns'),
        examsColumns: columns('Reports_Exams_Profile_Columns'),
      }
    default:
      return {}
  }
}
